import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconBuildingBank,
  IconCircleMinus,
  IconMapPin,
  IconPencil,
  IconPlayerPause,
  IconPlayerPlay,
  IconTrash,
  IconWheat,
} from '@tabler/icons-react';
import SaleStatusBadge from '@/components/app/sell/SaleStatusBadge';
import ExchangeStatusBadge from '@/components/app/sell/ExchangeStatusBadge';
import ExchangeOrientationCard from '@/components/app/sell/ExchangeOrientationCard';
import SaleOfferTerms from '@/components/app/sell/SaleOfferTerms';
import UpdatedDateBadge from '@/components/app/buy/UpdatedDateBadge';
import BestPriceLink from '@/components/common/BestPriceLink';
import { sellService } from '@/api/services/SellService';
import { SALE_TREE_QUERY_KEY, useExchangeOrientation, useExchangeSession, useExchangeStatuses, useSaleOffer } from '@/hooks/useSell';
import { useSaleBestOffersMap } from '@/hooks/useBestOffers';
import { bestOfferLink, bestPriceTone } from '@/lib/bestOffer';
import { canPublishToExchange, exchangeUiState, isFrozenByExchange } from '@/lib/exchangeOffer';
import { isVisibleInSell, mainQuality } from '@/lib/sellOffer';
import { makeSellEditUrl } from '@/lib/sellLinks';
import { formatMoney, formatOrgName } from '@/lib/format';
import { getApiMessage, isServiceUnavailable } from '@/api/errors';
import { ROUTES } from '@/routes/paths';

// Деталь одного оффера продажи: сводка (статус/качество/объём/цена) + «Условия предложения».
// Действия жизненного цикла (пауза/возобновление/удаление) — только для ACTIVE/SUSPENDED; оффер
// в сделке/продан показываем только для чтения с подсказкой искать его в «Сделки».
export default function SellOfferView() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const orderId = Number(params.get('orderId'));
  const address = params.get('address');
  const addressTitle = params.get('addressTitle');
  const goods = params.get('goods');
  const year = Number(params.get('year'));

  const valid = !Number.isNaN(orderId) && address && addressTitle && goods && !Number.isNaN(year);
  const { data: offer, isLoading, isError, error, refetch } = useSaleOffer(valid ? orderId : null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const bestOffer = useSaleBestOffersMap().get(orderId);
  // Биржевой статус тянем только для биржевого оффера; сессия — для доступности публикации.
  const { data: exchangeStatuses, isLoading: statusesLoading } = useExchangeStatuses(offer?.toExchange ? [orderId] : []);
  const { data: session } = useExchangeSession();
  // Ориентир цены (нетто-FCA) — только для биржевого оффера.
  const { data: orientation } = useExchangeOrientation(orderId, Boolean(offer?.toExchange));

  const backUrl = ROUTES.APP_SELL;

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ['saleOffer', orderId] });
    queryClient.invalidateQueries({ queryKey: ['saleOffers', address, addressTitle, goods, year] });
    queryClient.invalidateQueries({ queryKey: SALE_TREE_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['exchangeStatuses'] });
  };
  const onActionError = (err: unknown): void => {
    notifications.show({
      color: 'red',
      message: isServiceUnavailable(err) ? t('auth.errors.unavailable') : (getApiMessage(err) ?? t('auth.errors.generic')),
    });
  };

  const suspend = useMutation({
    mutationFn: () => sellService.suspendOffer(orderId),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'lime', message: t('app.sell.lot.suspended') });
    },
    onError: onActionError,
  });
  const unsuspend = useMutation({
    mutationFn: () => sellService.unsuspendOffer(orderId),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'lime', message: t('app.sell.lot.resumed') });
    },
    onError: onActionError,
  });
  const removeOffer = useMutation({
    mutationFn: () => sellService.deleteOffer(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saleOffers', address, addressTitle, goods, year] });
      queryClient.invalidateQueries({ queryKey: SALE_TREE_QUERY_KEY });
      notifications.show({ color: 'lime', message: t('app.sell.lot.deleted') });
      navigate(backUrl);
    },
    onError: onActionError,
  });

  const publishExchange = useMutation({
    mutationFn: () => sellService.publishToExchange(orderId),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'lime', message: t('app.sell.exchange.published') });
    },
    onError: onActionError,
  });
  const removeExchange = useMutation({
    mutationFn: () => sellService.removeFromExchange(orderId),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'lime', message: t('app.sell.exchange.removed') });
    },
    onError: onActionError,
  });

  function confirmDelete(): void {
    modals.openConfirmModal({
      title: t('app.sell.lot.deleteTitle'),
      children: <Text size="sm">{t('app.sell.lot.deleteConfirm')}</Text>,
      labels: { confirm: t('app.sell.del.confirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => removeOffer.mutate(),
    });
  }

  function confirmRemoveFromExchange(): void {
    modals.openConfirmModal({
      title: t('app.sell.exchange.removeTitle'),
      children: <Text size="sm">{t('app.sell.exchange.removeConfirm')}</Text>,
      labels: { confirm: t('app.sell.exchange.removeAction'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => removeExchange.mutate(),
    });
  }

  if (!valid) {
    return <Navigate to={ROUTES.APP_SELL} replace />;
  }

  const quality = offer ? mainQuality(offer) : null;
  const actionable = offer ? isVisibleInSell(offer) : false;
  const suspended = offer?.status === 'SUSPENDED';
  const exState = offer ? exchangeUiState(offer, exchangeStatuses?.[orderId]) : null;
  const isExchange = exState !== null;
  // Пока статус биржи грузится — состояние биржевого оффера неизвестно, действия не показываем.
  const exUnresolved = Boolean(offer?.toExchange) && statusesLoading;
  const frozen = isFrozenByExchange(exState); // живая заявка: правка/удаление заморожены
  const canPublish = !exUnresolved && canPublishToExchange(exState);
  const editable = actionable && !exUnresolved && !frozen && exState !== 'MATCHED';
  const inDeal = actionable && exState === 'MATCHED'; // сматчилось на бирже, платформенный статус ещё ACTIVE
  const failReason = exState === 'FAILED' ? exchangeStatuses?.[orderId]?.reason : null;

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Anchor component={Link} to={backUrl} size="sm" c="brand.7">
          <Group gap={4} wrap="nowrap">
            <IconArrowLeft size={16} />
            {t('app.sell.offerBack')}
          </Group>
        </Anchor>

        {isError ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
            <Group justify="space-between" wrap="nowrap">
              <Text>{isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</Text>
              <Button variant="subtle" size="compact-sm" onClick={() => refetch()}>
                {t('common.retry')}
              </Button>
            </Group>
          </Alert>
        ) : isLoading || !offer ? (
          <Stack gap="sm">
            <Skeleton h={120} />
            <Skeleton h={220} />
          </Stack>
        ) : (
          <>
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <ThemeIcon variant="light" color="brand" size={44} radius="md" style={{ flexShrink: 0 }}>
                <IconWheat size={26} />
              </ThemeIcon>
              <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
                <Group gap={8} wrap="wrap" align="center">
                  <Title order={2}>{offer.goodsType ?? goods}</Title>
                  <SaleStatusBadge status={offer.status} />
                  <UpdatedDateBadge date={offer.updatedDate} />
                </Group>
                <Group gap={6} wrap="nowrap">
                  <IconMapPin size={16} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />
                  <Text fw={500} style={{ flexShrink: 0 }}>
                    {formatOrgName(offer.addressTitle ?? addressTitle)}
                  </Text>
                  <Text size="sm" c="gray.7" truncate title={offer.address ?? address ?? undefined}>
                    · {offer.address ?? address}
                  </Text>
                </Group>
              </Stack>
            </Group>

            <Card withBorder padding="lg">
              <Group justify="space-between" wrap="wrap" gap="md" align="flex-start">
                <Group gap={6} wrap="wrap">
                  {quality ? (
                    <Badge variant="default" tt="none" fw={500} radius="sm" c="gray.9" size="lg">
                      {quality}
                    </Badge>
                  ) : null}
                  {offer.prepaymentInfo?.name ? (
                    <Badge variant="default" tt="none" fw={500} radius="sm" c="gray.9" size="lg">
                      {offer.prepaymentInfo.name}
                    </Badge>
                  ) : null}
                  <Badge variant="default" tt="none" fw={500} radius="sm" c="gray.9" size="lg">
                    {offer.weightTon != null ? `${formatMoney(offer.weightTon)} ${t('app.sell.tons')}` : '—'}
                  </Badge>
                  <ExchangeStatusBadge state={exState} loading={exUnresolved} size="lg" />
                </Group>
                <Stack gap={0} align="flex-end">
                  <Group gap={6} align="baseline">
                    <Text fz={26} fw={700} c="brand.9">
                      {offer.price != null ? formatMoney(offer.price) : '—'}
                    </Text>
                    <Text size="sm" c="gray.8">
                      {t('app.sell.rubPerTon')}
                    </Text>
                  </Group>
                </Stack>
              </Group>
            </Card>

            {actionable && !isExchange && bestOffer?.bestPrice != null ? (
              // Побуждающий CTA: под ваше предложение есть спрос — лучшая встречная цена покупателя.
              // Для биржевых офферов ориентиром служит BID стакана (добавляется отдельным блоком в Ф5).
              <Card withBorder padding="md" bg="lime.0" style={{ borderColor: 'var(--mantine-color-brand-4)' }}>
                <Group justify="space-between" wrap="wrap" gap="sm">
                  <Text size="sm" c="gray.8">
                    {t('app.sell.offerBestPriceHint')}
                  </Text>
                  <BestPriceLink
                    to={bestOfferLink(bestOffer)}
                    price={bestOffer.bestPrice}
                    action="sell"
                    tone={bestPriceTone(bestOffer.advantage)}
                    advantage={bestOffer.advantage}
                  />
                </Group>
              </Card>
            ) : null}

            {actionable && isExchange && orientation ? (
              <ExchangeOrientationCard orientation={orientation} />
            ) : null}

            {!actionable || inDeal ? (
              <Alert color="brand" variant="light" icon={<IconAlertTriangle size={18} />}>
                {t('app.sell.offerInDeal')}
              </Alert>
            ) : exUnresolved ? (
              <Skeleton h={40} />
            ) : (
              <Stack gap="sm">
                {failReason ? (
                  // Причина отказа биржи — чтобы продавец понял, что исправить перед повторной публикацией.
                  <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
                    {t('app.sell.exchange.failedReason', { reason: failReason })}
                  </Alert>
                ) : null}
                {frozen ? (
                  <Alert color="brand" variant="light" icon={<IconBuildingBank size={18} />}>
                    {t('app.sell.exchange.frozenHint')}
                  </Alert>
                ) : null}
                <Group gap="sm" wrap="wrap">
                  {editable ? (
                    <Button
                      component={Link}
                      to={makeSellEditUrl(orderId)}
                      variant="default"
                      leftSection={<IconPencil size={16} />}
                    >
                      {t('app.sell.lot.edit')}
                    </Button>
                  ) : null}

                  {!isExchange && suspended ? (
                    <Button
                      variant="default"
                      leftSection={<IconPlayerPlay size={16} />}
                      loading={unsuspend.isPending}
                      onClick={() => unsuspend.mutate()}
                    >
                      {t('app.sell.lot.resume')}
                    </Button>
                  ) : null}
                  {!isExchange && !suspended ? (
                    <Button
                      variant="default"
                      leftSection={<IconPlayerPause size={16} />}
                      loading={suspend.isPending}
                      onClick={() => suspend.mutate()}
                    >
                      {t('app.sell.lot.suspend')}
                    </Button>
                  ) : null}

                  {isExchange && canPublish ? (
                    <Button
                      color="brand"
                      leftSection={<IconBuildingBank size={16} />}
                      loading={publishExchange.isPending}
                      disabled={!session?.open}
                      onClick={() => publishExchange.mutate()}
                    >
                      {t(exState === 'FAILED' ? 'app.sell.exchange.republish' : 'app.sell.exchange.publish')}
                    </Button>
                  ) : null}
                  {isExchange && frozen ? (
                    <Button
                      color="red"
                      variant="light"
                      leftSection={<IconCircleMinus size={16} />}
                      loading={removeExchange.isPending}
                      onClick={confirmRemoveFromExchange}
                    >
                      {t('app.sell.exchange.removeAction')}
                    </Button>
                  ) : null}

                  {editable ? (
                    <Button
                      color="red"
                      variant="light"
                      leftSection={<IconTrash size={16} />}
                      loading={removeOffer.isPending}
                      onClick={confirmDelete}
                    >
                      {t('app.sell.lot.delete')}
                    </Button>
                  ) : null}
                </Group>
                {isExchange && canPublish && session && !session.open ? (
                  <Text size="xs" c="gray.7">
                    {t('app.sell.exchange.windowClosedHint', { start: session.windowStart, end: session.windowEnd })}
                  </Text>
                ) : null}
              </Stack>
            )}

            <SaleOfferTerms offer={offer} />
          </>
        )}
      </Stack>
    </Container>
  );
}
